import React, { useContext, useEffect, useState } from "react";
import {
  Switch,
  Route,
  Redirect,
  useLocation,
  useParams
} from "react-router-dom";
import {
  Login,
  Login2FA,
  LoginWithAuthLink,
  Schedule,
  ScheduleScreener,
  AppointmentCard,
  AppointmentResult,
  AppointmentUnavailable,
  AppointmentPayDisclaimer,
  AppointmentPending,
  AppointmentPayError,
  AppointmentPaySuccess,
  AppointmentAuth,
  Dashboard,
  LoginVerify,
  VaxYesRegister,
  VaxYesVerify,
  Chat,
  AppointmentVaxCertificate,
  ScheduleVerifyToken,
  GGVScheduleVerifyAvailableDates,
  GGVScheduleNoSlots
} from "./views";
import { HeaderProvider } from "./contexts";
import {
  CONSUMER_SESSION_AUTH,
  CONSUMER_SESSION_LOCATION,
  CONSUMER_SESSION_VERIFICATION_TOKEN,
  BRAND_GGT,
  BRAND_GGV,
  BRAND_GGD,
  ROUTE_PATHS,
  PARTNERS
} from "./constants";
import * as queryString from "query-string";
import PropTypes from "prop-types";
import { BrandThemeContext } from "./contexts/BrandThemeProvider";
import { useAuthRedirect } from "./hooks";
import { PartnerRedirect } from "./views/Partner/PartnerRedirect";
import {
  VaxYesRegisterStep,
  VaxYesRegisterSuccess,
  VaxYesRegisterPayment,
  VaxYesRegisterPaymentCallback,
  VaxYesBoosterRegisterStep
} from "./views/VaxYes/Register";
import { VaxYesBoosterSuccess } from "./views/VaxYes/Register/VaxYesBoosterSuccess";

/**
 * Switch brand based on string
 *
 * @param {string} brand
 * @param {function} setThemeName
 */
const switchBrand = (brand, setThemeName) => {
  if (!brand) return;

  setThemeName(brand);
};

const useLocationFromUrl = () => {
  const location = useLocation();

  useEffect(() => {
    const { search } = location;

    if (!search) return;

    const { location_id = "" } = queryString.parse(search);

    if (location_id) {
      sessionStorage.setItem(CONSUMER_SESSION_LOCATION, location_id);
    }
  }, [location]);
};

/**
 * Log page views and events to Google Tag Manager
 */
const useGoogleTagManager = () => {
  const location = useLocation();

  useEffect(() => {
    const _gtm = (window.dataLayer = window.dataLayer || []);
    _gtm.push({ event: "pageview", page: { path: location.pathname } });
  }, [location]);
};

/**
 * Log page views to Xandr
 */
const useXandrTracking = () => {
  const location = useLocation();

  useEffect(() => {
    if (!pixie) return;

    pixie("event", "PageView");
  }, [location]);
};

const PartnerRoute = ({ RenderComponent }) => {
  const { partner_code = "fit2fly" } = useParams();
  const [partner, setPartner] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!partner) return;

    setLoading(false);
  }, [partner]);

  useEffect(() => {
    if (PARTNERS[partner_code]) {
      setPartner(PARTNERS[partner_code]);
    } else {
      setLoading(false);
    }
  }, [partner_code]);

  if (loading) return null;
  if (!partner) return <Redirect to="/" />;

  return <RenderComponent partner={partner} partnerCode={partner_code} />;
};

const MultiBrandRoute = ({ children, ...rest }) => {
  const location = useLocation();
  const { setThemeName } = useContext(BrandThemeContext);

  useEffect(() => {
    const { search = {} } = location;
    const { brand = "" } = queryString.parse(search);

    switchBrand(brand, setThemeName);
  }, []);

  return <Route {...rest}>{children}</Route>;
};

const PrivateRoute = ({ children, brand, showHeader = true, ...rest }) => {
  const { pathname } = useLocation();
  const { setThemeName } = useContext(BrandThemeContext);
  const { setRedirectUrl } = useAuthRedirect();

  useEffect(() => {
    if (!pathname || sessionStorage.getItem(CONSUMER_SESSION_AUTH)) return;

    setRedirectUrl(pathname);
  }, [pathname]);

  useEffect(() => {
    switchBrand(brand, setThemeName);
  }, [brand]);

  return (
    <Route
      {...rest}
      render={({ location }) =>
        sessionStorage.getItem(CONSUMER_SESSION_AUTH) ? (
          <>
            {showHeader ? (
              <HeaderProvider brand={brand}>{children}</HeaderProvider>
            ) : (
              children
            )}
          </>
        ) : (
          <Redirect
            to={{
              pathname: `/login`,
              search: location.search,
              state: { from: location }
            }}
          />
        )
      }
    />
  );
};

PrivateRoute.propTypes = {
  brand: PropTypes.oneOf([BRAND_GGV, BRAND_GGD, BRAND_GGT])
};

const Routes = () => {
  if (process.env.REACT_APP_SENTRY_ENV === "production") {
    useGoogleTagManager();
    useXandrTracking();
  }

  useLocationFromUrl();

  return (
    <Switch>
      {/* Patient routes */}

      <PrivateRoute
        path={`${ROUTE_PATHS.GGV.SCHEDULE_START_WITH_TOKEN}/:verification_token/:group_code?`}
        brand={BRAND_GGV}
      >
        <ScheduleVerifyToken
          RenderSuccess={({ groupCode, verificationToken }) => (
            <>
              {sessionStorage.setItem(
                CONSUMER_SESSION_VERIFICATION_TOKEN,
                verificationToken
              )}
              <GGVScheduleVerifyAvailableDates
                groupCode={groupCode}
                RenderSuccess={({ groupCode }) => (
                  <Redirect
                    to={`${ROUTE_PATHS.GGV.SCHEDULE_START}/${groupCode}`}
                  />
                )}
                RenderFailure={() => (
                  <GGVScheduleNoSlots groupCode={groupCode} />
                )}
              />
            </>
          )}
          RenderFailure={() => <Redirect to="/" />}
        />
      </PrivateRoute>

      <PrivateRoute
        path={`${ROUTE_PATHS.GGV.SCHEDULE_START}/:group_code?`}
        brand={BRAND_GGV}
      >
        {!sessionStorage.getItem(CONSUMER_SESSION_VERIFICATION_TOKEN) ? (
          <Redirect to="/" />
        ) : (
          <ScheduleScreener brand={BRAND_GGV} />
        )}
      </PrivateRoute>

      <PrivateRoute
        path={`${ROUTE_PATHS.GGT.SCHEDULE_START}/:group_code?`}
        brand={BRAND_GGT}
      >
        <ScheduleScreener brand={BRAND_GGT} />
      </PrivateRoute>

      <PrivateRoute
        path={`${ROUTE_PATHS.GGT.SCHEDULE_BASE}/:step?`}
        brand={BRAND_GGT}
      >
        <Schedule brand={BRAND_GGT} />
      </PrivateRoute>

      <PrivateRoute
        path={`${ROUTE_PATHS.VAXYES.REGISTER_BASE}/success`}
        brand={BRAND_GGV}
        exact
      >
        <VaxYesRegisterSuccess />
      </PrivateRoute>

      <PrivateRoute
        path={`${ROUTE_PATHS.VAXYES.REGISTER_BASE}/payment`}
        brand={BRAND_GGV}
        exact
      >
        <VaxYesRegisterPayment />
      </PrivateRoute>

      <PrivateRoute
        path={`${ROUTE_PATHS.VAXYES.REGISTER_BASE}/start`}
        brand={BRAND_GGV}
        exact
      >
        <VaxYesRegisterStep />
      </PrivateRoute>

      <PrivateRoute
        path={`${ROUTE_PATHS.VAXYES.REGISTER_BASE}`}
        brand={BRAND_GGV}
        exact
      >
        <VaxYesRegister />
      </PrivateRoute>

      <PrivateRoute
        path={`${ROUTE_PATHS.VAXYES.REGISTER_BASE_BOOSTER}/success`}
        brand={BRAND_GGV}
        exact
      >
        <VaxYesBoosterSuccess />
      </PrivateRoute>

      <PrivateRoute
        path={`${ROUTE_PATHS.VAXYES.REGISTER_BASE_BOOSTER}`}
        brand={BRAND_GGV}
        exact
      >
        <VaxYesBoosterRegisterStep />
      </PrivateRoute>

      <MultiBrandRoute path={`${ROUTE_PATHS.VAXYES.VERIFY}`} exact>
        <VaxYesVerify />
      </MultiBrandRoute>

      <MultiBrandRoute
        path={`${ROUTE_PATHS.VAXYES.REGISTER_BASE}/callback`}
        exact
      >
        <VaxYesRegisterPaymentCallback />
      </MultiBrandRoute>

      <PrivateRoute
        path={`${ROUTE_PATHS.GGV.SCHEDULE_BASE}/:step?`}
        brand={BRAND_GGV}
      >
        <Schedule brand={BRAND_GGV} />
      </PrivateRoute>

      <PrivateRoute exact path={`/`}>
        <Dashboard />
      </PrivateRoute>

      <PrivateRoute exact path={`/appointment/pending`} showHeader={false}>
        <AppointmentPending />
      </PrivateRoute>

      <PrivateRoute path={`/appointment/:appointment_id/pay/success`}>
        <AppointmentPaySuccess />
      </PrivateRoute>

      <PrivateRoute exact path={`/appointment/:appointment_id/pay/error`}>
        <AppointmentPayError />
      </PrivateRoute>

      <PrivateRoute
        exact
        path={`/appointment/vax-certificate`}
        showHeader={false}
        brand={BRAND_GGV}
      >
        <AppointmentVaxCertificate />
      </PrivateRoute>

      <PrivateRoute exact path={`/appointment/pay`}>
        <AppointmentPayDisclaimer />
      </PrivateRoute>

      {/* Public Routes */}

      <MultiBrandRoute path={`/chat`} exact>
        <Chat />
      </MultiBrandRoute>

      <MultiBrandRoute path={`/r/:token`} exact>
        <AppointmentResult />
      </MultiBrandRoute>

      <MultiBrandRoute path={`/appointment/unavailable`} exact>
        <AppointmentUnavailable />
      </MultiBrandRoute>

      <MultiBrandRoute path={`/appointment/:appointment_id/auth`} exact>
        <AppointmentAuth />
      </MultiBrandRoute>

      <MultiBrandRoute path={`/appointment/:appointment_id/:dob`} exact>
        <AppointmentCard />
      </MultiBrandRoute>

      <Redirect
        from="/appointment/:appointment_id"
        to="/appointment/:appointment_id/auth"
      />

      {/* Prevent breaking old links to GGT schedule */}
      <Redirect from="/schedule/:step" to={ROUTE_PATHS.GGT.SCHEDULE_START} />

      <MultiBrandRoute path={`/login/2FA`} exact>
        <Login2FA />
      </MultiBrandRoute>

      <MultiBrandRoute path={`/login/verify`} exact>
        <LoginVerify />
      </MultiBrandRoute>

      <MultiBrandRoute path={`/login`} exact>
        <Login />
      </MultiBrandRoute>

      <MultiBrandRoute path={`/login/group/:group_code?`} exact>
        <Login />
      </MultiBrandRoute>

      <Route path={`/:phone/:token/:brand?`} component={LoginWithAuthLink} />

      <Route
        path={`/volaris`}
        exact
        render={() => (
          <PartnerRoute
            RenderComponent={({ partner, partnerCode }) => (
              <PartnerRedirect
                partner={partner}
                to={`${ROUTE_PATHS.GGT.SCHEDULE_START}/VOLARIS?partner=${partnerCode}`}
              />
            )}
          />
        )}
      />

      <Route
        path={`/aeromexico`}
        exact
        render={() => (
          <PartnerRoute
            RenderComponent={({ partner, partnerCode }) => (
              <PartnerRedirect
                partner={partner}
                to={`${ROUTE_PATHS.GGT.SCHEDULE_START}/AEROMEXICO?partner=${partnerCode}`}
              />
            )}
          />
        )}
      />

      <Redirect to={`/login`} />
    </Switch>
  );
};

export { Routes };
